import { useApiInfiniteQuery } from "@gladis/api-client";
import type { ActivityFeedPageDto } from "@gladis/shared";
import { Checkbox, EmptyState, ErrorState, LoadingState } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type { ActivityDestination } from "../../catalog/destinations.js";
import { ActivityRow } from "./ActivityRow.js";

/**
 * The activity feed: the household's events, newest first, fifty at a time.
 *
 * ==========================================================================================
 * WHEN IT STOPS ASKING FOR MORE
 * ==========================================================================================
 *
 * On `nextCursor === null`, and on nothing else. NOT on a short page — under "Hide system events" the
 * server may return fewer than fifty while more matching entries remain further back, so a client that
 * stopped counting rows would truncate the feed silently. The server is the only thing that knows whether
 * there is more, and `null` is how it says there is not.
 *
 * ==========================================================================================
 * THE TOGGLE IS IN THE QUERY KEY, AND IN NO STORAGE
 * ==========================================================================================
 *
 * Flipping it changes the key, so TanStack starts a fresh paging sequence from the newest matching entry
 * rather than trying to reconcile pages fetched under the other filter. That is also why the filter must be
 * a server predicate: pages already in the cache were selected under the old setting, and re-filtering them
 * client-side would leave half-empty pages behind.
 *
 * It is `useState` and not `localStorage` or a search param — FR-AL8 says the state is not persisted, so a
 * reload starts with system events visible. A member who wants them hidden is making a decision about the
 * next thirty seconds of reading, not a preference.
 */
export interface ActivityFeedProps {
  /** Navigation is the application's job. This package imports no router. */
  onOpen: (destination: ActivityDestination) => void;
}

export function ActivityFeed({ onOpen }: ActivityFeedProps): ReactNode {
  const [hideSystem, setHideSystem] = useState(false);

  const feed = useApiInfiniteQuery<ActivityFeedPageDto>(
    ["activity", { hideSystem }],
    (cursor) => {
      const params = new URLSearchParams();
      if (hideSystem) params.set("hideSystem", "true");
      if (cursor !== undefined) params.set("cursor", cursor);
      const query = params.toString();
      return query === "" ? "/activity" : `/activity?${query}`;
    },
    { nextPageParam: (page) => page.nextCursor ?? undefined },
  );

  const sentinel = useRef<HTMLDivElement | null>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = feed;

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) return;

    /*
     * An IntersectionObserver rather than a scroll listener: it fires once when the sentinel comes into
     * view instead of on every frame of scrolling, and it needs no knowledge of the scroll container —
     * which matters because the container here is the page, not this component.
     *
     * `isFetchingNextPage` is checked inside the callback as well as in the effect's dependencies. The
     * observer can fire twice before React re-renders, and two overlapping requests for the same page
     * would append the same fifty entries twice.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      // Ahead of the fold, so the next page is usually there by the time the reader arrives.
      { rootMargin: "200px" },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const t = useT();
  const entries = feed.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <div className="col-span-full">
      <div className="mb-4 flex items-center justify-between">
        <Checkbox
          label={t("activity.hideSystemEvents")}
          checked={hideSystem}
          onChange={(event) => {
            setHideSystem(event.target.checked);
          }}
          data-testid="hide-system-events"
        />
        {/* The only count worth showing is how much has been READ, because the server deliberately does
            not compute a total — that would be a second query for a number nothing needs. */}
        {entries.length > 0 ? (
          <span className="text-xs text-muted">
            {t("activity.entriesLoaded", { count: entries.length })}
          </span>
        ) : null}
      </div>

      {feed.isPending ? <LoadingState /> : null}

      {feed.isError ? <ErrorState>{t("activity.loadFailed")}</ErrorState> : null}

      {!feed.isPending && !feed.isError && entries.length === 0 ? (
        <EmptyState>{t("activity.empty")}</EmptyState>
      ) : null}

      {entries.length > 0 ? (
        <div className="border-t border-line" data-testid="activity-list">
          {entries.map((entry) => (
            <ActivityRow key={entry.id} entry={entry} onOpen={onOpen} />
          ))}
        </div>
      ) : null}

      {/* The sentinel is rendered only while there is more, so the observer cannot fire at the end of a
          fully-read feed. */}
      {hasNextPage ? (
        <div ref={sentinel} className="py-6 text-center text-xs text-muted">
          {isFetchingNextPage ? t("activity.loadingMore") : " "}
        </div>
      ) : null}

      {!hasNextPage && entries.length > 0 ? (
        <p className="py-6 text-center text-xs text-muted">{t("activity.endOfHistory")}</p>
      ) : null}
    </div>
  );
}
